import type { ChangeEvent, FormEvent } from 'react'

import { api } from '@setplex/tria-api'
import {
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { or } from 'patronum'
import { model as i18n } from '~/core/i18n'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { SEARCH_CONTENT_TYPE } from '~/features/analytix'
import { bindSearchAutocompleteRoute } from '~/features/search-autocomplete'
import {
  featuresVoiceSearchModel,
  truncateVoiceSearchResult,
} from '~/features/voice-search'
import { modules } from '~/shared/config'
import { MINIMUM_QUERY_LENGTH_OF_SEARCH_INPUT } from '~/shared/constants/strings'
import { checkIsSearchQueryValid, nay } from '~/shared/helpers'
import { SEARCH } from '~/shared/routes'
import { searchInit } from '../../root/model'
import { SEARCH_REQUEST_DELAY, TOOLTIP_VISIBLE_DELAY } from '../constants'
import { $isOnRegularSearchPage } from '../regularSearchPageState'
import { resetSearchOnProfileSwitch } from '../resetOnProfileSwitch'
import { formatSearchInputPlaceholder } from './lib'

// Events

export const handleChange = createEvent<{
  '<event>': ChangeEvent<HTMLInputElement>
}>()
const handleSubmit = createEvent<{ '<event>': FormEvent<HTMLFormElement> }>()
const deleteValue = createEvent()
export const resetSearchFieldState = createEvent()

// Effects

let timeout: ReturnType<typeof setTimeout>
const timeoutFx = createEffect<void, void>(
  () =>
    new Promise((resolve) => {
      clearInterval(timeout)
      timeout = setTimeout(resolve, SEARCH_REQUEST_DELAY)
    })
)
const cancelTimeoutFx = createEffect<void, void>(() => clearTimeout(timeout))

let timeoutTooltip: ReturnType<typeof setTimeout>
const timeoutTooltipFx = createEffect<void, void>(
  () =>
    new Promise((resolve) => {
      clearInterval(timeoutTooltip)
      timeoutTooltip = setTimeout(resolve, TOOLTIP_VISIBLE_DELAY)
    })
)
const cancelTimeoutTooltipFx = createEffect<void, void>(() =>
  clearTimeout(timeoutTooltip)
)

// Stores

const $placeholder = combine(
  {
    i18n: i18n.$t,
    tvEnabled: modules.$tvEnabled,
    vodEnabled: modules.$vodEnabled,
    tvShowEnabled: modules.$tvShowEnabled,
    eventsEnabled: modules.$liveEventEnabled,
  },
  ({ i18n: { t }, tvEnabled, vodEnabled, tvShowEnabled, eventsEnabled }) => {
    return formatSearchInputPlaceholder({
      t,
      tvEnabled,
      vodEnabled,
      tvShowEnabled,
      eventsEnabled,
    })
  }
)

const $isRequestsPending = or(
  api.globalSearch.getSearchFx.pending,
  api.globalSearch.loadSearchEventsFx.pending,
  api.globalSearch.loadSearchTvChannelsFx.pending,
  api.globalSearch.loadSearchTvShowsFx.pending,
  api.globalSearch.loadSearchVodsFx.pending
)

export const $searchValue = createStore<string>('')
  .on(handleChange, (_, data) => data['<event>'].target.value)
  .reset([deleteValue, resetSearchFieldState])

export const $isVisibleTooltip = createStore<boolean>(false).reset([
  searchInit,
  resetSearchFieldState,
])
export const $tooltipTitle = combine(i18n.$t, ({ t }) =>
  t('search_min_lengths_hint', {
    count: MINIMUM_QUERY_LENGTH_OF_SEARCH_INPUT,
  })
)

// Connections

sample({
  clock: handleChange,
  target: [timeoutFx, timeoutTooltipFx],
})

sample({
  clock: handleSubmit,
  target: [cancelTimeoutFx, cancelTimeoutTooltipFx],
})

// Disable the default behavior for the onSubmit, so that the search field value is not automatically set in the url and, accordingly, the page is not refreshing
sample({
  clock: [handleSubmit, timeoutFx.done],
  source: {
    searchValue: $searchValue,
    pending: $isRequestsPending,
  },
  filter: ({ searchValue, pending }) =>
    checkIsSearchQueryValid(searchValue) && nay(pending),
  fn: ({ searchValue }) => {
    const query = searchValue.replace(/^\s+|\s+$/gm, '')
    return SEARCH.resultsWithQuery({
      query,
      typeOfSearch: SEARCH_CONTENT_TYPE.manual,
    })
  },
  target: router.navigatePush,
})

// navigate to initial search page if user enter not valid search query directly in url
sample({
  clock: searchInit,
  filter: ({ query }) =>
    query.has('q') && !checkIsSearchQueryValid(query.get('q') || ''),
  fn: () => SEARCH.ROOT,
  target: router.navigatePush,
})

// synchronize search string from url with store on every entry (empty when
// there is no valid query). Kept as the current (untrimmed) value when the
// query already matches it: a debounced search re-enters this same route
// with its own trimmed query, and resetting/re-syncing here would drop
// whitespace the user is still typing, cutting the following keystrokes off
// from it (FP-4248).
sample({
  clock: searchInit,
  source: $searchValue,
  fn: (searchValue, { query }) => {
    const q = query.get('q') || ''
    if (!query.has('q') || !checkIsSearchQueryValid(q)) return ''
    return q === searchValue.replace(/^\s+|\s+$/gm, '') ? searchValue : q
  },
  target: $searchValue,
})

sample({
  clock: [handleSubmit, timeoutTooltipFx.done],
  source: $searchValue,
  fn: (value) => !checkIsSearchQueryValid(value),
  target: $isVisibleTooltip,
})

sample({
  clock: $searchValue,
  filter: (value: string) => checkIsSearchQueryValid(value),
  fn: (value) => !checkIsSearchQueryValid(value),
  target: $isVisibleTooltip,
})

// Voice search result: navigate directly with voice source for analytics
sample({
  clock: featuresVoiceSearchModel.applyVoiceSearchResult,
  source: $isOnRegularSearchPage,
  filter: (isOnPage, result) => {
    const q = truncateVoiceSearchResult(result)
    return isOnPage && checkIsSearchQueryValid(q)
  },
  fn: (_, result) =>
    SEARCH.resultsWithQuery({
      query: truncateVoiceSearchResult(result),
      typeOfSearch: SEARCH_CONTENT_TYPE.voice,
    }),
  target: router.navigatePush,
})

bindSearchAutocompleteRoute({
  $searchValue,
  $isActive: $isOnRegularSearchPage,
  typingClock: handleChange,
  // Regular search uses two routes (initial + results). If we listed initial left
  // here, suggestions would reset on navigate initial → results after submit and the menu
  // would disappear. Autocomplete reset when fully leaving search is done in results/model
  resetAutocompleteOn: [],
  getNavigateToOnPick: (query) =>
    SEARCH.resultsWithQuery({
      query,
      typeOfSearch: SEARCH_CONTENT_TYPE.manual,
    }),
  voiceSearchResultClock: featuresVoiceSearchModel.applyVoiceSearchResult,
})

sample({
  clock: resetSearchOnProfileSwitch,
  target: resetSearchFieldState,
})

register($placeholder, '$search/placeholder')
register($searchValue, '$search/searchValue')
register($isVisibleTooltip, '$search/isVisibleTooltip')
register($tooltipTitle, '$search/tooltipTitle')
register(handleChange, 'search/handleChange')
register(handleSubmit, 'search/handleSubmit')
register(deleteValue, 'search/deleteValue')
